import {
  DesignerSettings,
  ExperienceType,
  ProductLayout,
} from '@taopix/taopix-core/dist/types/types';
import {AuthUserFlow} from './AuthUserFlow';
import {OrderUserFlow} from './OrderUserFlow';
import {ProjectStartUserFlow} from './ProjectStartUserFlow';
import {FlightCheckUserFlow} from './FlightCheckUserFlow';
import {BaseUserFlow, UserFlowDirection, UserFlowList, UserFlowStep} from './BaseUserFlow';
import {UserFlow} from './UserFlow';
import {OptionsAndExtrasUserFlow} from './OptionsAndExtrasUserFlow';
import {SaveUserFlow} from './SaveUserFlow';
import {UserInterfaceState} from '../../Components/Context/UserInterface/UserInterfaceContext';
import {ShareUserFlow} from './ShareUserFlow';
import {UpgradeUserFlow} from './UpgradeUserFlow';
import {AssistantUserFlow} from './AssistantUserFlow';
import {ChangeUserFlow} from './ChangeProductUserFlow';
import {FromAssistantUserFlow} from './FromAssistantUserFlow';
import {ForceLoginUserFlow} from './ForceLoginUserFlow';

export enum UserFlowType {
  UserFlow = 'userflow',
  Start = 'start',
  Auth = 'auth',
  Order = 'order',
  FlightCheck = 'flightcheck',
  OptionsAndExtras = 'optionsandextras',
  Assistant = 'assistant',
  Save = 'save',
  Share = 'share',
  Upgrade = 'upgrade',
  Change = 'change',
  FromAssistant = 'fromassistant',
  ForceLogin = 'forcelogin',
}

const userFlowMap = {
  [UserFlowType.UserFlow]: UserFlow,
  [UserFlowType.Auth]: AuthUserFlow,
  [UserFlowType.OptionsAndExtras]: OptionsAndExtrasUserFlow,
  [UserFlowType.Order]: OrderUserFlow,
  [UserFlowType.FlightCheck]: FlightCheckUserFlow,
  [UserFlowType.Save]: SaveUserFlow,
  [UserFlowType.Share]: ShareUserFlow,
  [UserFlowType.Upgrade]: UpgradeUserFlow,
  [UserFlowType.Change]: ChangeUserFlow,

  // Need to be activated last as they need access to the previous userflows.
  [UserFlowType.Start]: ProjectStartUserFlow,
  [UserFlowType.Assistant]: AssistantUserFlow,
  [UserFlowType.FromAssistant]: FromAssistantUserFlow,

  [UserFlowType.ForceLogin]: ForceLoginUserFlow,
};

enum StepStatus {
  InActive = 'inactive',
  Active = 'active',
}

type UserFlowMap = typeof userFlowMap;
export type UserFlowMapKeys = keyof UserFlowMap;

export default class UserFlowFacade {
  static #instance: UserFlowFacade;
  static activeStages: Set<string> = new Set();
  static pointer = UserFlowType.UserFlow;
  static userFlows: Record<any, BaseUserFlow> = {};
  static availableUserFlows = Object.keys(userFlowMap);
  static stepsMap = new Map();

  static get instance(): UserFlowFacade {
    if (!UserFlowFacade.#instance) {
      UserFlowFacade.#instance = new UserFlowFacade();
    }

    return UserFlowFacade.#instance;
  }

  static setActiveStep = (step: string): void => {
    const parts = step.split('.');

    if (this.stepsMap.has(parts[0])) {
      const map = this.stepsMap.get(parts[0]);
      if (parts[1] === UserFlowStep.None) {
        map.active = false;
        [...map.steps.keys()].forEach(step => this.setInActiveStep(`${parts[0]}.${step}`));
      } else {
        [...map.steps.keys()].forEach(step => this.setInActiveStep(`${parts[0]}.${step}`));
        if (map.steps.has(parts[1])) {
          map.active = true;
          map.steps.set(parts[1], StepStatus.Active);
        } else if (parts[0] === parts[1]) {
          map.active = true;
        }
      }
    }
  };

  static setInActiveStep = (step: string): void => {
    const parts = step.split('.');

    if (!this.stepsMap.get(parts[0])) {
      return;
    }

    if (parts.length === 1) {
      const map = this.stepsMap.get(parts[0]);
      map.active = false;
      [...map.steps.keys()].forEach(step => this.setInActiveStep(`${parts[0]}.${step}`));
    } else if (this.stepsMap.has(parts[0])) {
      const map = this.stepsMap.get(parts[0]);
      map.active = false;
      if (map.steps.has(parts[1])) {
        map.steps.set(parts[1], StepStatus.InActive);
      }
    }
  };

  public getActiveStages = (): string[] => {
    return [...UserFlowFacade.activeStages];
  };

  public getStep = (): number => {
    return UserFlowFacade.userFlows[UserFlowFacade.pointer].getStep();
  };

  public addSkipStep(direction: UserFlowDirection, step: string): void {
    UserFlowFacade.userFlows[UserFlowFacade.pointer].addSkipStep(direction, step);
  }

  public removeSkipStep(direction: UserFlowDirection, step: string): void {
    UserFlowFacade.userFlows[UserFlowFacade.pointer].removeSkipStep(direction, step);
  }

  /**
   * Sets the experience object on each userflow..
   *
   * @param experience Experience object to set.
   */
  static setExperience = (experience: ExperienceType): void => {
    if (Object.values(UserFlowFacade.userFlows).length > 0) {
      for (const key in UserFlowFacade.userFlows) {
        UserFlowFacade.userFlows[key].setExperience(experience);
      }
    }
  };

  /**
   * Sets the designer settings on each userflow.
   *
   * @param designerSettings Designer settings object to set.
   */
  static setDesignerSettings = (designerSettings: DesignerSettings): void => {
    if (Object.values(UserFlowFacade.userFlows).length > 0) {
      for (const key in UserFlowFacade.userFlows) {
        UserFlowFacade.userFlows[key].setDesignerSetting(designerSettings);
      }
    }
  };

  /**
   * Sets the UI settings on each userflow.
   *
   * @param uiState UI settings object to set.
   */
  static setUI = (uiState: UserInterfaceState): void => {
    if (Object.values(UserFlowFacade.userFlows).length > 0) {
      for (const key in UserFlowFacade.userFlows) {
        UserFlowFacade.userFlows[key].setUi(uiState);
      }
    }
  };

  /**
   * Sets the product layout on each userflow.
   *
   * @param productLayout layout object to set.
   */
  static setProductLayout = (productLayout: ProductLayout): void => {
    if (Object.values(UserFlowFacade.userFlows).length > 0) {
      for (const key in UserFlowFacade.userFlows) {
        UserFlowFacade.userFlows[key].setProductLayout(productLayout);
      }
    }
  };

  /**
   * Wrapper for UserFlow.getSteps().
   *
   * @returns The steps for the current UserFlow.
   */
  public getSteps = (userFlowType: UserFlowType): UserFlowList => {
    if (UserFlowFacade.userFlows[userFlowType]) {
      return UserFlowFacade.userFlows[userFlowType].getSteps();
    }

    return [];
  };

  static getMapKeyByValue(map, searchValue) {
    for (const [key, value] of map.entries()) {
      if (value === searchValue) return key;
    }

    return UserFlowStep.None;
  }

  /**
   * Wrapper for UserFlow.getCurrentStep().
   * If the current step uses the dot notation, then the getCurrentStep will be fired for the related user flow.
   * Also handles replacements for the auth user flow.
   *
   * @returns The current step.
   */
  public getCurrentStep = (userFlowType?: UserFlowType): string => {
    const currentUserFlow = [...UserFlowFacade.activeStages.values()].pop() as UserFlowType;
    if (!userFlowType && currentUserFlow) {
      return UserFlowFacade.userFlows[currentUserFlow].getCurrentStep();
    }

    if (!userFlowType || 0 === Object.keys(UserFlowFacade.userFlows).length) {
      return UserFlowStep.None;
    }

    if (!UserFlowFacade.userFlows.hasOwnProperty(userFlowType)) return UserFlowStep.None;
    return UserFlowFacade.userFlows[userFlowType].getCurrentStep();
  };

  /**
   * Wrapper for UserFlow.setPreviousStep().
   * If the current step uses the dot notation, then the setPreviousStep will be fired for the related user flow.
   */
  public setPreviousStep = () => {
    const currentUserFlow = [...UserFlowFacade.activeStages.values()].pop() as UserFlowType;

    // Call the underlying userflow.
    const userFlow = UserFlowFacade.userFlows[currentUserFlow];
    const lastStep = userFlow.getCurrentStep();

    // Update step map settings the previous step to inactive.
    UserFlowFacade.setInActiveStep(lastStep);

    userFlow.setPreviousStep();
    const newStep = userFlow.getCurrentStep();

    if (UserFlowStep.None === newStep.split('.')[1]) {
      UserFlowFacade.setInActiveStep(lastStep);
      this.deactivateUserFlow(currentUserFlow);

      const lastStepType = lastStep.split('.')[0];
      // If the last step is from a different userFlow, then deactivate that as well.
      if (lastStepType !== currentUserFlow) {
        this.deactivateUserFlow(lastStepType as UserFlowType);
      }
    } else {
      const lastUserFlow = lastStep.split('.')[0];
      if (lastUserFlow !== currentUserFlow) {
        // Deactivate the last step if it's different to the current userflow.
        UserFlowFacade.setInActiveStep(lastUserFlow);
      }
      UserFlowFacade.setActiveStep(newStep);
    }
  };

  public getPreviousStep = () => {
    return UserFlowFacade.userFlows[UserFlowFacade.pointer].getPreviousStep();
  };

  public setCurrentStep = (newStep: string) => {
    const currentUserFlow = [...UserFlowFacade.activeStages.values()].pop() as UserFlowType;
    let newCurrentStep = newStep;
    let parts = newStep.split('.');

    // Update the underlying userFlow.
    // Will either set the new step, or the one forced from the main userflow.
    UserFlowFacade.userFlows[parts[0]].setCurrentStep(newCurrentStep);

    // Get the new step in case the underlying userflow has modified it.
    newCurrentStep = UserFlowFacade.userFlows[parts[0]].getCurrentStep();

    parts = newCurrentStep.split('.');
    if (UserFlowFacade.userFlows.hasOwnProperty(parts[0])) {
      const mainUserFlowStep = UserFlowFacade.userFlows[currentUserFlow].getCurrentStep();

      if (parts[1] === UserFlowStep.None) {
        // Ask the current userflow if it can change if it's trying to close.
        const canContinue = UserFlowFacade.userFlows[currentUserFlow].canChangeStep(newStep);
        if (canContinue) {
          // Move on the main userflow.
          if (mainUserFlowStep.split('.')[0] === parts[0]) {
            this.setNextStep();
            UserFlowFacade.setInActiveStep(newStep);
          }
        } else if (UserFlowFacade.userFlows[currentUserFlow].getCanCancel()) {
          UserFlowFacade.setInActiveStep(newStep);
          this.deactivateUserFlow(currentUserFlow);
        } else {
          newCurrentStep = UserFlowFacade.userFlows[currentUserFlow].getCurrentStep();
        }
      }

      UserFlowFacade.setActiveStep(newCurrentStep);

      // If the step doesn't exist then deactivate the userflow, or the step was set to none.
      if (
        ((UserFlowStep.None !== parts[1] &&
          !UserFlowFacade.stepsMap.get(parts[0]).steps.has(parts[1])) ||
          UserFlowStep.None === parts[1]) &&
        UserFlowFacade.activeStages.size > 1 &&
        currentUserFlow === parts[0]
      ) {
        this.deactivateUserFlow(currentUserFlow);
      }
    }

    // eslint-disable-next-line
    // console.table(UserFlowFacade.stepsMap);
  };

  public setInactive = (userFlowType: UserFlowType) => {
    UserFlowFacade.setInActiveStep(userFlowType);

    if (UserFlowFacade.pointer !== userFlowType) {
      // Ask the main userflow if it can be closed.
      const currentStep = UserFlowFacade.userFlows[UserFlowFacade.pointer].getCurrentStep();
      if (currentStep.split('.')[0] === userFlowType) {
        UserFlowFacade.setActiveStep(currentStep);
      }
    }
  };

  public resetStep = () => {
    UserFlowFacade.userFlows[UserFlowFacade.pointer].setStep(0);
  };

  public setNextStep = () => {
    const currentUserFlow = [...UserFlowFacade.activeStages.values()].pop() as UserFlowType;

    // Call the underlying userflow.
    const userFlow = UserFlowFacade.userFlows[currentUserFlow];
    const lastStep = userFlow.getCurrentStep();

    // Update step map settings the previous step to inactive.
    UserFlowFacade.setInActiveStep(lastStep);

    userFlow.setNextStep();
    const newStep = userFlow.getCurrentStep();

    if (UserFlowStep.None === newStep.split('.')[1]) {
      UserFlowFacade.setInActiveStep(lastStep);
      this.deactivateUserFlow(currentUserFlow);
      const lastStepType = lastStep.split('.')[0];
      // If the last step is from a different userFlow, then deactivate that as well.
      if (lastStepType !== currentUserFlow) {
        this.deactivateUserFlow(lastStepType as UserFlowType);
      }
    } else {
      const lastUserFlow = lastStep.split('.')[0];
      if (lastUserFlow !== currentUserFlow) {
        // Deactivate the last step if it's different to the current userflow.
        UserFlowFacade.setInActiveStep(lastUserFlow);
      }
      UserFlowFacade.setActiveStep(newStep);
    }

    // eslint-disable-next-line
    // console.table(UserFlowFacade.stepsMap);
  };

  public getNextStep = (userFlowType: UserFlowType) => {
    return UserFlowFacade.userFlows[userFlowType].getNextStep();
  };

  /**
   * Creates the instances for each available user flow.
   * If create is called again and userFlowStage doesn't match the pointer,
   * it will recreate the userflows in case any states have change, e.g.
   * the user has now logged in.
   *
   * @param userFlowStage The current user flow to use.
   * @param designerSettings Designer settings.
   * @param productLayout Product layout details
   * @param uiState UI state details
   * @param experience Experience object
   * @returns New instance of the UserFlowFacade
   */
  static create<K extends UserFlowMapKeys>(
    designerSettings: DesignerSettings,
    productLayout: ProductLayout,
    uiState: UserInterfaceState,
    experience: ExperienceType
  ): UserFlowFacade {
    if (!designerSettings) {
      return UserFlowFacade.instance;
    }
    if (!productLayout) {
      return UserFlowFacade.instance;
    }
    if (!experience || Object.keys(experience).length === 0) {
      return UserFlowFacade.instance;
    }

    if (Object.keys(UserFlowFacade.userFlows).length === 0) {
      // Initialise once, the userFlow will update when activateUserFlow is called.
      // Only the main flow won't update.
      UserFlowFacade.availableUserFlows.forEach(userFlow => {
        try {
          UserFlowFacade.initialiseUserFlow(
            userFlow,
            designerSettings,
            productLayout,
            uiState,
            experience
          );
        } catch (e) {
          console.error(e);
        }
      });
    }

    if (0 === this.stepsMap.size) {
      UserFlowFacade.addStepsMap();
      UserFlowFacade.instance.setUserFlowStage(UserFlowFacade.pointer);
    }

    // eslint-disable-next-line
    // console.table(UserFlowFacade.stepsMap);

    return UserFlowFacade.instance;
  }

  static init = (): UserFlowFacade => {
    UserFlowFacade.availableUserFlows.forEach(userFlowStage => {
      try {
        UserFlowFacade.userFlows[userFlowStage] = new userFlowMap[userFlowStage]();
      } catch (e) {
        console.error(e);
      }
    });
    return UserFlowFacade.instance;
  };

  /**
   * Creates the instances for the provided user flows and will call configure and setStep methods.
   *
   * @param userFlowStage The current user flow to use.
   * @param designerSettings Designer settings.
   * @param productLayout Product layout details
   * @param uiState UI state details
   * @param experience Experience object
   * @returns New instance of the UserFlowFacade
   */
  static initialiseUserFlow(
    userFlowStage: string,
    designerSettings: DesignerSettings,
    productLayout: ProductLayout,
    uiState: UserInterfaceState,
    experience: ExperienceType
  ) {
    if (!UserFlowFacade.userFlows[userFlowStage]) {
      UserFlowFacade.userFlows[userFlowStage] = new userFlowMap[userFlowStage]();
    }
    UserFlowFacade.userFlows[userFlowStage]
      .setDesignerSetting(designerSettings)
      .setProductLayout(productLayout)
      .setUi(uiState)
      .setExperience(experience as any)
      .setStage(userFlowStage);
    UserFlowFacade.userFlows[userFlowStage].setSteps(
      UserFlowFacade.userFlows[userFlowStage].configure()
    );
  }

  static addStepsMap = () => {
    if (UserFlowFacade.stepsMap.size === 0) {
      UserFlowFacade.stepsMap = Object.entries(
        UserFlowFacade.userFlows[UserFlowType.UserFlow].getAvailableSteps()
      ).reduce((acc, step) => {
        acc.set(step[0], {
          active: false,
          steps: (step[1] as any).reduce((acc2, step2) => {
            acc2.set(step2, StepStatus.InActive);
            return acc2;
          }, new Map()),
        });
        return acc;
      }, new Map());
    }
  };

  public clearActiveStages = () => {
    this.getActiveStages().forEach(stage => {
      this.deactivateUserFlow(stage as UserFlowType);
    });

    UserFlowFacade.activeStages.clear();
  };

  public setUserFlowStage = (stage: UserFlowType, ref = this) => {
    const currentStage = UserFlowFacade.pointer;
    UserFlowFacade.pointer = stage;

    if (Object.values(UserFlowFacade.userFlows).length === 0) return;

    this.deactivateUserFlow(currentStage);
    // this.clearActiveStages();
    UserFlowFacade.instance.resetStep();
    this.activateUserFlow(UserFlowFacade.pointer);

    // eslint-disable-next-line
    // console.table(UserFlowFacade.stepsMap);
  };

  public activateUserFlow = (userFlowType: UserFlowType): void => {
    if (Object.keys(UserFlowFacade.userFlows).length === 0) {
      return;
    }

    // Rebuild.
    UserFlowFacade.userFlows[userFlowType].setSteps(
      UserFlowFacade.userFlows[userFlowType].configure()
    );
    UserFlowFacade.userFlows[userFlowType].setStep(0);

    const currentStep = UserFlowFacade.userFlows[userFlowType].getCurrentStep();

    if (
      0 === UserFlowFacade.userFlows[userFlowType].getSteps().length &&
      UserFlowType.UserFlow !== userFlowType
    ) {
      // Don't activate step if it has no steps.
      return;
    }

    UserFlowFacade.setActiveStep(currentStep);
    UserFlowFacade.activeStages.add(userFlowType);
  };

  public deactivateUserFlow = (userFlowType: UserFlowType): void => {
    if (UserFlowType.UserFlow === userFlowType) return;

    const currentStep = UserFlowFacade.userFlows[userFlowType].getCurrentStep();
    UserFlowFacade.setInActiveStep(currentStep);
    if (UserFlowFacade.activeStages.has(userFlowType)) {
      UserFlowFacade.activeStages.delete(userFlowType);
    }
  };

  public isUserFlowActive = (userFlowType: UserFlowType): boolean => {
    if (!UserFlowFacade.stepsMap.has(userFlowType)) return false;
    return UserFlowFacade.stepsMap.get(userFlowType).active;
  };

  public isStepActive = (userFlowType: UserFlowType, userFlowStep: UserFlowStep): boolean => {
    if (!UserFlowFacade.stepsMap.has(userFlowType)) return false;
    return UserFlowFacade.stepsMap.get(userFlowType).steps.get(userFlowStep) === StepStatus.Active;
  };

  public canChangeStep = (step: string): boolean => {
    return UserFlowFacade[UserFlowFacade.pointer].canChangeStep(step);
  };
}
